import React, { useRef } from 'react'
import { useDrawingArea, useXScale, useYScale } from '@mui/x-charts/hooks'
import { ScaleLinear } from 'd3-scale'
import { styled } from '@mui/material/styles'
import { Boundaries } from '../ScatterPlot'

const StyledPath = styled('path')(({ theme }) => ({
    fill: 'none',
    stroke: theme.palette.text.primary,
    shapeRendering: 'crispEdges',
    strokeWidth: 1,
    pointerEvents: 'none',
    strokeDasharray: '5 2',
}))

export const ScatterSelector = ({
    onChange,
    onZoom,
    svgRef,
}: {
    onChange: (
        pos: {
            minX: number
            maxX: number
            minY: number
            maxY: number
        } | null,
    ) => void
    onZoom: (b: Boundaries) => void
    svgRef: React.RefObject<SVGSVGElement | null>
}) => {
    const { left, top, width, height } = useDrawingArea()
    const clearButtonRef = useRef<SVGRectElement | null>(null)
    const zoomButtonRef = useRef<SVGRectElement | null>(null)

    const yAxisScale = useYScale() as ScaleLinear<unknown, unknown>
    const xAxisScale = useXScale() as ScaleLinear<unknown, unknown>

    const currentSelection = useRef<{ startX: number; endX: number; startY: number; endY: number } | null>(null)
    const [drawEndPosition, setDrawEndPosition] = React.useState<null | { x: number; y: number }>(null)
    const [drawStartPosition, setDrawStartPosition] = React.useState<null | { x: number; y: number }>(null)
    const topRightPosition =
        !drawEndPosition || !drawStartPosition
            ? null
            : {
                  x: Math.max(drawStartPosition.x, drawEndPosition.x),
                  y: Math.min(drawStartPosition.y, drawEndPosition.y),
              }

    React.useEffect(() => {
        const element = svgRef.current
        if (element === null) {
            return () => {}
        }

        const handleMouseMove = (event: MouseEvent) => {
            const mouseX = event.offsetX
            const mouseY = event.offsetY
            const position = {
                x: Math.max(Math.min(left + width, mouseX), left),
                y: Math.max(Math.min(top + height, mouseY), top),
            } // clamp to the drawing area

            setDrawStartPosition((pos) => pos || position)
            setDrawEndPosition(position)

            if (!currentSelection.current) {
                currentSelection.current = {
                    startX: mouseX,
                    endX: mouseX,
                    startY: mouseY,
                    endY: mouseY,
                }
                return
            }

            currentSelection.current.endX = mouseX
            currentSelection.current.endY = mouseY
        }

        const handleMouseDown = (event: MouseEvent) => {
            if (event.button !== 0) return

            if (event.target === clearButtonRef?.current) {
                setDrawEndPosition(null)
                setDrawStartPosition(null)
                currentSelection.current = null

                return
            }

            if (event.target === zoomButtonRef?.current) {
                setDrawEndPosition(null)
                setDrawStartPosition(null)

                if (!currentSelection.current) {
                    console.warn('currentSelection.current is unexpectadly null.')
                    return
                }

                const x = [
                    xAxisScale.invert(currentSelection.current.startX),
                    xAxisScale.invert(currentSelection.current.endX),
                ].sort((a, b) => a - b)
                const y = [
                    yAxisScale.invert(currentSelection.current.startY),
                    yAxisScale.invert(currentSelection.current.endY),
                ].sort((a, b) => a - b)
                onZoom({
                    minX: x[0],
                    maxX: x[1],
                    minY: y[0],
                    maxY: y[1],
                })
                currentSelection.current = null

                return
            }

            element.addEventListener('mousemove', handleMouseMove)
        }

        const handleMouseUp = (event: MouseEvent) => {
            if (event.button !== 0) return

            element.removeEventListener('mousemove', handleMouseMove)

            if (!currentSelection.current) {
                onChange(null)
                return
            }

            const x = [
                xAxisScale.invert(currentSelection.current.startX),
                xAxisScale.invert(currentSelection.current.endX),
            ].sort((a, b) => a - b)
            const y = [
                yAxisScale.invert(currentSelection.current.startY),
                yAxisScale.invert(currentSelection.current.endY),
            ].sort((a, b) => a - b)
            onChange({
                minX: x[0],
                maxX: x[1],
                minY: y[0],
                maxY: y[1],
            })
        }

        element.addEventListener('mousedown', handleMouseDown)
        document.body.addEventListener('mouseup', handleMouseUp)

        return () => {
            element.removeEventListener('mousedown', handleMouseDown)
            document.body.removeEventListener('mouseup', handleMouseUp)
        }
    }, [height, left, top, width, svgRef, onChange, onZoom, currentSelection, yAxisScale, xAxisScale])

    if (drawEndPosition === null) {
        return null
    }

    return (
        <React.Fragment>
            {drawStartPosition && [
                <StyledPath
                    key='horizontal-1'
                    d={`M ${drawStartPosition.x} ${drawStartPosition.y} L ${drawEndPosition.x} ${drawStartPosition.y}`}
                />,
                <StyledPath
                    key='vertical-1'
                    d={`M ${drawStartPosition.x} ${drawStartPosition.y} L ${drawStartPosition.x} ${drawEndPosition.y}`}
                />,
                <StyledPath
                    key='horizontal-2'
                    d={`M ${drawStartPosition.x} ${drawEndPosition.y} L ${drawEndPosition.x} ${drawEndPosition.y}`}
                />,
                <StyledPath
                    key='vertical-2'
                    d={`M ${drawEndPosition.x} ${drawStartPosition.y} L ${drawEndPosition.x} ${drawEndPosition.y}`}
                />,
            ]}
            {topRightPosition && (
                <g>
                    <svg x={topRightPosition.x} y={Math.max(topRightPosition.y - 25, 0)}>
                        <path
                            d={`M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8zM12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z`}
                        />
                        <rect
                            width='25'
                            height='25'
                            fillOpacity={0}
                            ref={clearButtonRef}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                        />
                    </svg>

                    <svg x={topRightPosition.x - 30} y={Math.max(topRightPosition.y - 25, 0)} fill='#1f1f1f'>
                        <path d='M0 0h24v24H0V0z' fill='none' />
                        <path d='M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' />
                        <path d='M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z' />

                        <rect
                            width='25'
                            height='25'
                            fillOpacity={0}
                            ref={zoomButtonRef}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                        />
                    </svg>
                </g>
            )}
        </React.Fragment>
    )
}
